// Compliance Service for InsuraCentral Frontend
// Integrates with existing API architecture and Supabase backend

import type { AxiosError } from "axios";
import { shouldToastApiError } from '@/app/_module/utils/apiResponseHandler';
import { showToast } from "../utils/show-toast";
import { transformErrorResponse } from "../utils/transform-error-response";
import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall, normalizeApiError } from "../utils/apiResponseHandler";
import type {
  ComplianceDashboard,
  ComplianceFramework,
  ComplianceControl,
  EvidenceItem,
  ComplianceAlert,
  ComplianceHistory,
  FrameworkDetails,
  ControlEvidence,
  CreateFrameworkRequest,
  UpdateFrameworkRequest,
  CreateControlRequest,
  UpdateControlRequest,
  UploadEvidenceRequest,
  VerifyEvidenceRequest,
  ComplianceReportRequest,
  ComplianceFilters,
  ComplianceSearchParams,
  ComplianceStats,
  ComplianceUpdate
} from "../types/compliance";

class ComplianceService {
  private handleError(error: unknown, operation: string): never {

    // Enhanced error logging for debugging
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError;

    }
    
    const normalized = normalizeApiError(error);
    const retrySuffix = typeof normalized.retryAfterSeconds === 'number' ? ` (retry after ${normalized.retryAfterSeconds}s)` : '';
    const message = normalized.message || `Failed to ${operation}`;
    // silent = MFA-window 403 the interceptor self-recovers — never toast it
    if (shouldToastApiError(normalized, operation)) { showToast(`${message}${retrySuffix}`) }
    const e = new Error(message) as Error & { code?: string | number; retryAfterSeconds?: number };
    e.code = normalized.code;
    e.retryAfterSeconds = normalized.retryAfterSeconds;
    throw e;
  }

  // Dashboard Methods
  async getDashboard(): Promise<ComplianceDashboard> {
    try {
      const response = await handleApiCall(() => api.get<ComplianceDashboard>({
        url: getApiUrl(endpoint.compliance.dashboard),
        auth: true
      }));
      return response; // handleApiCall already extracts data
    } catch (error) {
      // Provide better error handling for 403 errors
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 403) {

          throw new Error('You do not have sufficient permissions to access the compliance dashboard. System administrator privileges are required.');
        }
      }
      this.handleError(error, "Get Dashboard");
    }
  }

  async getStats(): Promise<ComplianceStats> {
    try {
      const response = await handleApiCall(() => api.get<ComplianceStats>({
        url: getApiUrl(endpoint.compliance.stats),
        auth: true
      }));
      return response; // handleApiCall already extracts data
    } catch (error) {
      this.handleError(error, "Get Stats");
    }
  }

  // Framework Methods
  async getFrameworks(filters?: ComplianceFilters): Promise<ComplianceFramework[]> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'dateRange' && typeof value === 'object') {
              params.append('startDate', value.start.toISOString());
              params.append('endDate', value.end.toISOString());
            } else {
              params.append(key, String(value));
            }
          }
        });
      }

      const response = await handleApiCall(() => api.get<ComplianceFramework[]>({
        url: `${getApiUrl(endpoint.compliance.frameworks)}?${params.toString()}`
      }));
      return response; // handleApiCall already extracts data
    } catch (error) {
      this.handleError(error, "Get Frameworks");
    }
  }

  async getFrameworkById(id: string): Promise<ComplianceFramework> {
    try {
      const response = await handleApiCall(() => api.get<ComplianceFramework>({
        url: getApiUrl(endpoint.compliance.getFramework(id))
      }));
      return response; // handleApiCall already extracts data
    } catch (error) {
      this.handleError(error, "Get Framework by ID");
    }
  }

  async createFramework(data: CreateFrameworkRequest): Promise<ComplianceFramework> {
    try {
      const response = await handleApiCall(() => api.post<ComplianceFramework>({
        url: getApiUrl(endpoint.compliance.frameworks),
        body: data
      }));

      showToast("Compliance framework created successfully");

      return response; // handleApiCall already extracts data
    } catch (error) {
      this.handleError(error, "Create Framework");
    }
  }

  async updateFramework(id: string, data: UpdateFrameworkRequest): Promise<ComplianceFramework> {
    try {
      const response = await handleApiCall(() => api.patch({
        url: getApiUrl(endpoint.compliance.getFramework(id)),
        body: data
      }));

      showToast("Compliance framework updated successfully");

      return response; // handleApiCall already extracts data
    } catch (error) {
      this.handleError(error, "Update Framework");
    }
  }

  async deleteFramework(id: string): Promise<void> {
    try {
      await handleApiCall(() => api.delete({
        url: getApiUrl(endpoint.compliance.getFramework(id))
      }));
      
      showToast("Compliance framework deleted successfully");
    } catch (error) {
      this.handleError(error, "Delete Framework");
    }
  }

  // Framework Details
  async getFrameworkDetails(id: string): Promise<FrameworkDetails> {
    try {
      const response = await api.get<FrameworkDetails>({
        url: getApiUrl(endpoint.compliance.getFrameworkDetails(id))
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Framework Details");
    }
  }

  // Control Methods
  async getControls(frameworkId?: string, filters?: ComplianceFilters): Promise<ComplianceControl[]> {
    try {
      const params = new URLSearchParams();
      if (frameworkId) params.append('frameworkId', frameworkId);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const response = await api.get<ComplianceControl[]>({
        url: `${getApiUrl(endpoint.compliance.controls)}?${params.toString()}`
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Controls");
    }
  }

  async getControlById(id: string): Promise<ComplianceControl> {
    try {
      const response = await api.get<ComplianceControl>({
        url: getApiUrl(endpoint.compliance.getControl(id))
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Control by ID");
    }
  }

  async createControl(data: CreateControlRequest): Promise<ComplianceControl> {
    try {
      const response = await api.post<ComplianceControl>({
        url: getApiUrl(endpoint.compliance.controls),
        body: data
      });
      
      showToast("Compliance control created successfully");
      
      return response.data;
    } catch (error) {
      this.handleError(error, "Create Control");
    }
  }

  async updateControl(id: string, data: UpdateControlRequest): Promise<ComplianceControl> {
    try {
      const response = await api.patch({
        url: getApiUrl(endpoint.compliance.getControl(id)),
        body: data
      });
      
      showToast("Compliance control updated successfully");
      
      return response.data as ComplianceControl;
    } catch (error) {
      this.handleError(error, "Update Control");
    }
  }

  async deleteControl(id: string): Promise<void> {
    try {
      await api.delete({
        url: getApiUrl(endpoint.compliance.getControl(id))
      });
      
      showToast("Compliance control deleted successfully");
    } catch (error) {
      this.handleError(error, "Delete Control");
    }
  }

  // Evidence Methods
  async getEvidence(controlId?: string, filters?: ComplianceFilters): Promise<EvidenceItem[]> {
    try {
      const params = new URLSearchParams();
      if (controlId) params.append('controlId', controlId);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const response = await api.get<EvidenceItem[]>({
        url: `${getApiUrl(endpoint.compliance.evidence)}?${params.toString()}`
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Evidence");
    }
  }

  async getEvidenceById(id: string): Promise<EvidenceItem> {
    try {
      const response = await api.get<EvidenceItem>({
        url: getApiUrl(endpoint.compliance.getEvidence(id))
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Evidence by ID");
    }
  }

  async uploadEvidence(data: UploadEvidenceRequest): Promise<EvidenceItem> {
    try {
      const formData = new FormData();
      formData.append('controlId', data.controlId);
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('evidenceType', data.evidenceType);
      if (data.file) {
        formData.append('file', data.file);
      }
      if (data.metadata) {
        formData.append('metadata', JSON.stringify(data.metadata));
      }

      const response = await api.post<EvidenceItem>({
        url: getApiUrl(endpoint.compliance.evidence),
        body: formData,
        options: {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      });
      
      showToast("Evidence uploaded successfully");
      
      return response.data;
    } catch (error) {
      this.handleError(error, "Upload Evidence");
    }
  }

  async verifyEvidence(data: VerifyEvidenceRequest): Promise<EvidenceItem> {
    try {
      const response = await api.patch({
        url: getApiUrl(endpoint.compliance.verifyEvidence(data.evidenceId)),
        body: {
          status: data.status,
          rejectionReason: data.rejectionReason
        }
      });
      
      showToast(`Evidence ${data.status.toLowerCase()} successfully`);
      
      return response.data as EvidenceItem;
    } catch (error) {
      this.handleError(error, "Verify Evidence");
    }
  }

  async deleteEvidence(id: string): Promise<void> {
    try {
      await api.delete({
        url: getApiUrl(endpoint.compliance.getEvidence(id))
      });
      
      showToast("Evidence deleted successfully");
    } catch (error) {
      this.handleError(error, "Delete Evidence");
    }
  }

  // Control Evidence
  async getControlEvidence(controlId: string): Promise<ControlEvidence> {
    try {
      const response = await api.get<ControlEvidence>({
        url: getApiUrl(endpoint.compliance.getControlEvidence(controlId))
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Control Evidence");
    }
  }

  // Alerts Methods
  async getAlerts(filters?: ComplianceFilters): Promise<ComplianceAlert[]> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const response = await api.get<ComplianceAlert[]>({
        url: `${getApiUrl(endpoint.compliance.alerts)}?${params.toString()}`
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Alerts");
    }
  }

  async markAlertAsRead(alertId: string): Promise<ComplianceAlert> {
    try {
      const response = await api.patch({
        url: getApiUrl(endpoint.compliance.markAlertAsRead(alertId))
      });
      
      return response.data as ComplianceAlert;
    } catch (error) {
      this.handleError(error, "Mark Alert as Read");
    }
  }

  async markAllAlertsAsRead(): Promise<void> {
    try {
      await api.post({
        url: getApiUrl(endpoint.compliance.markAllAlertsAsRead)
      });

      showToast("All alerts marked as read");
    } catch (error) {
      this.handleError(error, "Mark All Alerts as Read");
    }
  }

  // History Methods
  async getHistory(frameworkId?: string, controlId?: string, evidenceId?: string): Promise<ComplianceHistory[]> {
    try {
      const params = new URLSearchParams();
      if (frameworkId) params.append('frameworkId', frameworkId);
      if (controlId) params.append('controlId', controlId);
      if (evidenceId) params.append('evidenceId', evidenceId);

      const response = await api.get<ComplianceHistory[]>({
        url: `${getApiUrl(endpoint.compliance.history)}?${params.toString()}`
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get History");
    }
  }

  // Report Methods
  async generateReport(request: ComplianceReportRequest): Promise<Blob> {
    try {
      const response = await api.post<Blob>({
        url: getApiUrl(endpoint.compliance.generateReport),
        body: request,
        options: {
          responseType: 'blob'
        }
      });
      
      showToast("Report generated successfully");
      
      return response.data;
    } catch (error) {
      this.handleError(error, "Generate Report");
    }
  }

  // Assessment Methods
  async triggerAssessment(frameworkId?: string): Promise<void> {
    try {
      const body = frameworkId ? { frameworkId } : {};
      await api.post({
        url: getApiUrl(endpoint.compliance.triggerAssessment),
        body
      });
      
      showToast("Compliance assessment triggered successfully");
    } catch (error) {
      this.handleError(error, "Trigger Assessment");
    }
  }

  // Search Methods
  async searchCompliance(params: ComplianceSearchParams): Promise<{
    frameworks: ComplianceFramework[];
    controls: ComplianceControl[];
    evidence: EvidenceItem[];
    total: number;
  }> {
    try {
      const searchParams = new URLSearchParams();
      if (params.query) searchParams.append('query', params.query);
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(`filter_${key}`, String(value));
          }
        });
      }

      const response = await api.get<{
        frameworks: ComplianceFramework[];
        controls: ComplianceControl[];
        evidence: EvidenceItem[];
        total: number;
      }>({
        url: `${getApiUrl(endpoint.compliance.search)}?${searchParams.toString()}`
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Search Compliance");
    }
  }

  // Real-time Updates (WebSocket)
  subscribeToUpdates(callback: (update: ComplianceUpdate) => void): () => void {
    // This would integrate with your existing real-time service
    // For now, we'll implement a simple polling mechanism
    const interval = setInterval(async () => {
      try {
        const dashboard = await this.getDashboard();
        callback({
          type: 'DASHBOARD_UPDATED',
          data: dashboard,
          timestamp: new Date(),
          userId: '', // This would come from your auth context
          agencyId: undefined
        });
      } catch (error) {

      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }

  // Framework Settings Methods
  async getFrameworkSettings(frameworkId: string): Promise<any> {
    try {
      const response = await api.get({
        url: getApiUrl(`${endpoint.compliance.frameworks}/${frameworkId}/settings`),
        auth: true
      });
      return response.data;
    } catch (error) {
      this.handleError(error, "Get Framework Settings");
    }
  }

  async updateFrameworkSettings(frameworkId: string, settings: any): Promise<any> {
    try {
      const response = await api.patch({
        url: getApiUrl(`${endpoint.compliance.frameworks}/${frameworkId}/settings`),
        body: settings,
        auth: true
      });

      showToast(`Framework settings updated successfully`);

      return response.data;
    } catch (error) {
      this.handleError(error, "Update Framework Settings");
    }
  }
}

// Export singleton instance
export const complianceService = new ComplianceService();
export default complianceService;
